import type { RulesOutcome } from '~/models/rulesOpinion'
import type { Candidate } from './candidate'
import { assessConfidence } from './confidence'
import { type DerivedFacts, deriveFacts, type PhaseFacts } from './facts'
import type { RulesInputSnapshot } from './inputs/snapshot'
import { selectPrimary } from './prioritize'
import { RULESET_REGISTRY, type RulesetEntry } from './registry'
import {
  adherenceConcern,
  nutritionSourceConflict,
  plannedEndReached,
  recoveryConcern,
  strengthConcern,
} from './rulesets/shared'
import { DEFAULT_RULES_THRESHOLDS, type RulesThresholds } from './thresholds'
import { RULE_IDS, RULES_ENGINE_VERSION } from './version'

/**
 * The weekly rules engine (rules-engine.md, "Evaluation order").
 *
 * Pure: the same snapshot and thresholds always produce the same outcome. It
 * reads no clock — `evaluatedAt` arrives on the snapshot — touches no storage,
 * and formats nothing for display.
 *
 * Order, and why it is this order:
 *   1. validate context   — no phase means there is nothing to judge against
 *   2. phase coverage     — an unknown phase type stops here, never falling
 *                           through to another phase's rules (D-043)
 *   3. derive facts       — observations, no judgements
 *   4. data quality       — a week that cannot be compared is not interpreted
 *   5. gather candidates  — the phase ruleset plus every cross-phase family
 *   6. select and filter  — approved precedence, then the D-007 hard filter and
 *                           the one-action invariant
 *   7. confidence         — the full four-condition mapping (D-066)
 *
 * From Checkpoint 2.4 all five phases are implemented and the safety, adherence
 * and phase-end families run in every one of them, so the documented evaluation
 * order is now covered end to end.
 *
 * **This still may not be described as the complete Version 1 rules engine.**
 * D-043's completeness claim belongs to Checkpoint 2.7, after the export
 * contract catches up and the whole matrix is verified together.
 */
export function evaluateWeek(
  snapshot: RulesInputSnapshot,
  thresholds: RulesThresholds = DEFAULT_RULES_THRESHOLDS,
): RulesOutcome {
  const stamp = { engineVersion: RULES_ENGINE_VERSION, evaluatedAt: snapshot.evaluatedAt }

  const phase = snapshot.phase
  if (!phase) {
    return { outcome: 'contextUnavailable', reason: 'noActivePhase', ...stamp }
  }

  /* All five phases are implemented from Checkpoint 2.4, so this no longer
     guards an unwritten ruleset — it guards an UNKNOWN phase type, which
     restored or downgraded data can still produce. The lookup is therefore
     treated as possibly absent rather than trusted, and the outcome stays
     terminal: never another phase's logic, never a recommendation, never
     exported as an opinion (D-043). A test drives a phase type outside the
     union through this path, so the branch is reachable and provable rather
     than defensive padding. */
  const entry = RULESET_REGISTRY[phase.type] as RulesetEntry | undefined
  if (!entry?.implemented || !entry.evaluate) {
    return { outcome: 'phaseRulesUnavailable', phaseType: phase.type, ...stamp }
  }

  const facts = deriveFacts(snapshot, thresholds)
  const phaseFacts = facts.phase
  if (!phaseFacts) {
    return { outcome: 'contextUnavailable', reason: 'noActivePhase', ...stamp }
  }

  const candidates = gatherCandidates(snapshot, facts, phaseFacts, entry, thresholds)

  const prioritized = selectPrimary(candidates, snapshot.adherence, snapshot.nutrition) as Candidate
  const confidence = assessConfidence(
    facts,
    snapshot.adherence,
    snapshot.weeksHoldingSteady,
    thresholds,
  )

  return {
    outcome: 'opinion',
    category: prioritized.category,
    primaryAction: prioritized.primaryAction,
    supportingActions: prioritized.supportingActions,
    confidence: confidence.level,
    confidenceReason: confidence.reason,
    evidence: prioritized.evidence,
    watchItems: prioritized.watchItems,
    missingEvidence: prioritized.missingEvidence,
    rejectedAlternatives: prioritized.rejectedAlternatives,
    triggeredRules: [prioritized.ruleId],
    ...stamp,
  }
}

/**
 * Every rule that fired, for the selector to order (D-066).
 *
 * Two things short-circuit rather than joining the list, and both follow
 * rules-engine.md's evaluation order rather than the family precedence:
 *
 *  1. **A week that cannot be compared** is not interpreted at all. No phase can
 *     read a week it cannot measure (D-036).
 *  2. **A phase at its planned end** is step 1 of the documented order, ahead of
 *     safety. D-020 requires an explicit transition decision, and that decision
 *     *is* the safe action — telling someone to ease a phase that has already
 *     finished would be answering a question they are no longer asking.
 *
 * Everything else competes: the phase ruleset plus the cross-phase families,
 * which run in EVERY phase. That is what makes safety leading
 * `RULE_FAMILY_PRECEDENCE` mean something rather than being a fat-loss detail.
 */
function gatherCandidates(
  snapshot: RulesInputSnapshot,
  facts: DerivedFacts,
  phaseFacts: PhaseFacts,
  entry: RulesetEntry,
  thresholds: RulesThresholds,
): readonly Candidate[] {
  if (facts.weight.weeklyChangePercent === undefined) {
    return [notEnoughDataToCompare(facts.weight.entryCount)]
  }

  const atPlannedEnd = plannedEndReached(facts, phaseFacts)
  if (atPlannedEnd) return [atPlannedEnd]

  return [
    entry.evaluate?.(facts, { adherenceIsHigh: snapshot.adherence === 'high' }),
    recoveryConcern(facts, thresholds),
    strengthConcern(facts, thresholds),
    adherenceConcern(facts, snapshot.adherence),
    nutritionSourceConflict(facts, phaseFacts),
  ].filter((candidate): candidate is Candidate => candidate !== undefined)
}

/**
 * Too few weigh-ins to compare this week with the last one (D-036).
 *
 * Phase-independent: no phase can interpret a week that cannot be measured, so
 * this runs before dispatch and states exactly what would make next week
 * judgeable, as rules-engine.md requires of "gather more data".
 */
function notEnoughDataToCompare(entryCount: number): Candidate {
  return {
    category: 'gatherMoreData',
    primaryAction:
      'Weigh in on most days this week. Project Sola needs at least four weigh-ins in each of two consecutive weeks before it can compare them.',
    supportingActions: [],
    ruleId: RULE_IDS.dataInsufficientWeighIns,
    family: 'phaseProgress',
    leadingEvidence: 'weight',
    evidence: [
      {
        kind: 'dataQuality',
        statement: `${entryCount} weigh-in${entryCount === 1 ? '' : 's'} in the last seven days.`,
        value: entryCount,
        unit: 'entries',
      },
    ],
    watchItems: [],
    missingEvidence: [
      'Four or more weigh-ins in each of the current and previous seven-day windows',
    ],
    rejectedAlternatives: [
      {
        action: 'Judge progress from the weigh-ins available',
        reason:
          'Too few readings to separate a real trend from day-to-day fluctuation. A number here would look more certain than it is.',
      },
    ],
    reducesIntakeOrActivity: false,
  }
}
